<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import apiClient from '@/apiClient.js'
import { apiUrl } from '@/vars.js'
import staffService from '@/services/StaffService.js'
import useDebouncedFunction from '@/composables/useDebouncedFuncion'
import { useBreadcrumbStore } from '@/stores/BreadcrumbStore'
import { useAuthStore } from '@/stores/AuthStore'
import TrainingCourses from '@/components/TrainingCourses'
import ReferenceNumber from '@/components/ReferenceNumber.vue'
import JobsTrendChart from '@/components/JobsTrendChart.vue'
import JobsRoleMixChart from '@/components/JobsRoleMixChart.vue'
import EditButton from '@/components/EditButton.vue'
import SearchContact from '@/components/SearchContact'
import TableList from '@/components/TableList.vue'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()
const breadcrumbStore = useBreadcrumbStore()
const authStore = useAuthStore()

const STAFF_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'employment', label: 'Employment' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'jobs', label: 'Jobs Worked' },
  { id: 'payments', label: 'Payments', financeOnly: true },
]
const DEFAULT_TAB = 'overview'
const ONBOARDING_FIELD_LABELS = {
  contract: 'Contract',
  workingTimeDirectiveOptOut: 'Working Time Directive',
  pensionAutoEnrollmentOptOut: 'Pension Enrollment',
  hmrcNewStarterP45: 'HMRC Starter',
  reference1: 'Reference #1',
  reference2: 'Reference #2',
}

const editable = ref(true)
const staff = ref({})
const skills = ref([])
const selectedSkills = ref([])
const photoUrl = ref('')
const photoUploading = ref(false)
const badge = ref('')
const activeTab = ref(DEFAULT_TAB)
const jobsWorked = ref([])
const jobsLoading = ref(false)
const jobsLoaded = ref(false)
const jobsError = ref('')
const payments = ref([])
const paymentsLoading = ref(false)
const paymentsLoaded = ref(false)
const paymentsError = ref('')
const skillSearch = ref('')
const badgeTitleMode = ref('primary')
const isReady = ref(false)
const saving = ref(false)
const pendingSave = ref(false)
const lastSavedSignature = ref('')
const lastSavedBadgeSignature = ref('')
const toast = reactive({
  visible: false,
  message: '',
  tone: 'success',
})
let toastTimeout = null

const canViewPayments = computed(() => authStore.hasAnyRole(['finance-department', 'super']))
const tabs = computed(() =>
  STAFF_TABS.filter(tab => !tab.financeOnly || canViewPayments.value),
)
const normalizedSkills = computed(() =>
  Array.isArray(skills.value) ? skills.value : [],
)
const selectedSkillIdSet = computed(() =>
  new Set(selectedSkills.value.map(skill => Number(skill.id))),
)
const filteredSkills = computed(() => {
  const query = skillSearch.value.trim().toLowerCase()
  if (!query) {
    return normalizedSkills.value
  }
  return normalizedSkills.value.filter(skill =>
    String(skill.name || '').toLowerCase().includes(query),
  )
})
const badgeTitleLength = computed(() =>
  String(staff.value.badge_title_override ?? '').length,
)

const jobsTrendData = computed(() => {
  const currentMonth = dayjs().startOf('month')
  const months = Array.from({ length: 12 }, (_, index) =>
    currentMonth.subtract(11 - index, 'month'),
  )

  const seed = months.reduce((acc, month) => {
    acc[month.format('YYYY-MM')] = { total: 0, personnel: 0, driver: 0 }
    return acc
  }, {})

  jobsWorked.value.forEach(job => {
    if (!job.date) {
      return
    }
    const date = dayjs(job.date)
    if (!date.isValid()) {
      return
    }
    const key = date.format('YYYY-MM')
    if (!seed[key]) {
      return
    }

    seed[key].total += 1
    if (job.source === 'driver') {
      seed[key].driver += 1
    } else {
      seed[key].personnel += 1
    }
  })

  return {
    labels: months.map(month => month.format('MMM YY')),
    totals: months.map(month => seed[month.format('YYYY-MM')].total),
    personnel: months.map(month => seed[month.format('YYYY-MM')].personnel),
    drivers: months.map(month => seed[month.format('YYYY-MM')].driver),
  }
})

const jobsRoleMixData = computed(() => {
  const counts = jobsWorked.value.reduce((acc, job) => {
    const role = String(job.role || 'Unspecified').trim() || 'Unspecified'
    acc[role] = (acc[role] || 0) + 1
    return acc
  }, {})

  const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1])
  const topRoles = ranked.slice(0, 6)
  const otherCount = ranked.slice(6).reduce((sum, [, count]) => sum + count, 0)

  if (otherCount > 0) {
    topRoles.push(['Other', otherCount])
  }

  return {
    labels: topRoles.map(([label]) => label),
    values: topRoles.map(([, count]) => count),
  }
})
const hasPayrollClaims = job =>
  canViewPayments.value && Array.isArray(job?.payroll_claims) && job.payroll_claims.length > 0
const payrollClaimHours = job =>
  (job?.payroll_claims ?? [])
    .reduce((sum, claim) => sum + (Number(claim.claimed_hours) || 0), 0)
    .toFixed(2)
const payrollClaimSummary = job =>
  (job?.payroll_claims ?? [])
    .map(claim => {
      const product = claim.product?.short ?? claim.product?.name ?? claim.rate_code ?? 'Claim'
      const hours = Number(claim.claimed_hours) || 0
      const coworker = claim.co_worker?.contact?.name ?? claim.co_worker_name
      return `${product}: ${hours.toFixed(2)}h${coworker ? ` with ${coworker}` : ''}`
    })
    .join(' | ')

const getValidTab = tabId => {
  const requested = String(tabId ?? '').toLowerCase()
  return tabs.value.some(tab => tab.id === requested) ? requested : DEFAULT_TAB
}

const setActiveTab = tabId => {
  const nextTab = getValidTab(tabId)
  activeTab.value = nextTab
  if (route.query.tab !== nextTab) {
    router.replace({
      query: {
        ...route.query,
        tab: nextTab,
      },
    })
  }
  ensureTabDataLoaded(nextTab)
}

const showToast = (message, tone = 'success') => {
  toast.message = message
  toast.tone = tone
  toast.visible = true
  if (toastTimeout) {
    clearTimeout(toastTimeout)
  }
  toastTimeout = setTimeout(() => {
    toast.visible = false
  }, 3000)
}

const refreshActiveTabData = () => {
  if (activeTab.value === 'jobs') {
    loadJobsWorked({ force: true })
  }
  if (activeTab.value === 'payments' && canViewPayments.value) {
    loadPayments({ force: true })
  }
}

const onFileChange = event => {
  const file = event.target.files?.[0]
  if (!file) {
    return
  }
  uploadPhoto(file)
  event.target.value = ''
}

const uploadPhoto = async file => {
  if (!file || !staff.value.id) {
    return
  }
  photoUploading.value = true

  const formData = new FormData()
  formData.append('photo', file)

  try {
    await apiClient.post('/api/staff/' + staff.value.id + '/upload-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    await Promise.all([getPhoto(), getBadge()])
    showToast('Photo uploaded.')
  } catch (error) {
    console.error('Error uploading file', error)
    showToast('Unable to upload photo.', 'error')
  } finally {
    photoUploading.value = false
  }
}

const getPhoto = async () => {
  if (!staff.value.id) {
    return
  }
  try {
    const r = await apiClient.get('/api/staff/' + staff.value.id + '/photo')
    photoUrl.value = r.data.image
  } catch (error) {
    photoUrl.value = ''
  }
}

const getBadge = async () => {
  if (!staff.value.id) {
    return
  }
  try {
    const r = await apiClient.get('/api/staff/' + staff.value.id + '/badge')
    badge.value = r.data
  } catch (error) {
    badge.value = ''
  }
}

const getSkills = async () => {
  try {
    const r = await apiClient.get('/api/staff/skills')
    skills.value = r.data
  } catch (error) {
    skills.value = []
  }
}

const generateIdBadge = async () => {
  if (!confirm('Are you sure you want to void the existing ID badge?')) {
    return
  }
  try {
    const response = await apiClient.post('/api/staff/' + staff.value.id + '/badge')
    badge.value = response.data
    await getBadge()
    showToast('ID badge generated.')
  } catch (error) {
    showToast('Unable to generate ID badge.', 'error')
  }
}

const addEmergencyContact = async contact => {
  try {
    // Await the response from the API call
    const response = await apiClient.post(
      `/api/contact/${staff.value.contact.id}/emergency-contact`,
      {
        emergency_contact_id: contact.id,
      },
    )

    // Assuming a 200 OK response means success
    if (response && response.status === 200) {
      staff.value.contact.emergency_contacts.push(contact)
    }
  } catch (error) {
    console.error('Error adding emergency contact')
  }
}

const deleteEmergencyContact = async contact => {
  try {
    // Send the DELETE request
    await apiClient.delete(
      `/api/contact/${staff.value.contact.id}/emergency-contact/${contact.id}`,
    )

    // If successful, remove the contact from the emergency_contacts array
    const index = staff.value.contact.emergency_contacts.findIndex(
      c => c.id === contact.id,
    )
    if (index !== -1) {
      staff.value.contact.emergency_contacts.splice(index, 1)
    }
  } catch (error) {
    console.error('Failed to delete emergency contact:', error)
  }
}

const uploadOnboardingFile = async (event, label, fieldKey) => {
  const file = event.target.files?.[0]
  if (!file || !staff.value.id) {
    return
  }

  try {
    const response = await staffService.uploadFile(staff.value.id, label, file)
    if (response?.data?.file) {
      staff.value[fieldKey] = response.data.file
    }
    showToast(`${ONBOARDING_FIELD_LABELS[label]} uploaded.`)
  } catch (error) {
    const fallback = ONBOARDING_FIELD_LABELS[label]?.toLowerCase() ?? 'file'
    showToast(error?.response?.data?.message ?? `Unable to upload ${fallback}.`, 'error')
  } finally {
    event.target.value = ''
  }
}

const getSavePayload = () => {
  if (!staff.value.id || !staff.value.contact) {
    return null
  }

  const customBadgeTitle = String(staff.value.badge_title_override ?? '').trim()
  const selectedSkillIds = selectedSkills.value.map(skill => Number(skill.id))
  const normalizedPrimarySkill = selectedSkillIds.length === 0
    ? null
    : (selectedSkillIds.includes(Number(staff.value.primary_skill))
      ? Number(staff.value.primary_skill)
      : selectedSkillIds[0])

  return {
    _method: 'PUT',
    active: !!staff.value.active,
    employment_type: staff.value.employment_type || 'employee',
    joined_at: staff.value.joined_at ?? null,
    induction_at: staff.value.induction_at ?? null,
    contact: {
      name: staff.value.contact.name ?? null,
      nickname: staff.value.contact.nickname ?? null,
      email: staff.value.contact.email ?? null,
      phone: staff.value.contact.phone ?? null,
      sex: staff.value.contact.sex ?? null,
      dob: staff.value.contact.dob ?? null,
      addr: staff.value.contact.addr ?? null,
    },
    dbsNumber: staff.value.dbsNumber ?? null,
    dbsDate: staff.value.dbsDate ?? null,
    dbsUpdateService: staff.value.dbsUpdateService,
    drivingLicenseCheck: staff.value.drivingLicenseCheck ?? null,
    drivingAssessment: staff.value.drivingAssessment ?? null,
    notes: staff.value.notes ?? null,
    national_insurance: staff.value.national_insurance ?? null,
    tax_code: staff.value.tax_code ?? null,
    national_insurance_category: staff.value.national_insurance_category ?? null,
    registration_number: staff.value.registration_number ?? null,
    skills: selectedSkillIds,
    uniform_shirt: staff.value.uniform_shirt ?? null,
    uniform_jacket: staff.value.uniform_jacket ?? null,
    uniform_trousers: staff.value.uniform_trousers ?? null,
    primary_skill: normalizedPrimarySkill,
    badge_title_override: badgeTitleMode.value === 'custom' ? (customBadgeTitle || null) : null,
  }
}

const getSaveSignature = payload => JSON.stringify(payload)
const getBadgeSignature = payload => JSON.stringify({
  primary_skill: payload?.primary_skill ?? null,
  badge_title_override: payload?.badge_title_override ?? null,
})

const saveNow = async () => {
  if (!isReady.value || !staff.value.id) {
    return
  }

  const payload = getSavePayload()
  if (!payload) {
    return
  }

  const payloadSignature = getSaveSignature(payload)
  const badgeSignature = getBadgeSignature(payload)
  if (payloadSignature === lastSavedSignature.value) {
    return
  }
  if (saving.value) {
    pendingSave.value = true
    return
  }

  saving.value = true
  try {
    await apiClient.post(`/api/staff/${staff.value.id}`, payload)
    lastSavedSignature.value = payloadSignature
    const shouldReloadBadge = badgeSignature !== lastSavedBadgeSignature.value
    lastSavedBadgeSignature.value = badgeSignature
    if (shouldReloadBadge) {
      await getBadge()
    }
    showToast('Staff changes saved.')
  } catch (error) {
    showToast(error?.response?.data?.message ?? 'Unable to save staff changes.', 'error')
    console.error('Error saving staff details:', error)
  } finally {
    saving.value = false
    if (pendingSave.value) {
      pendingSave.value = false
      saveNow()
    }
  }
}

const { debouncedFunction: debouncedSave, disableTimer } = useDebouncedFunction(saveNow, 800)

const getStaff = async () => {
  isReady.value = false
  try {
    const response = await apiClient.get('/api/staff/' + route.params.id)
    staff.value = response.data
    staff.value.active = !!staff.value.active
    staff.value.employment_type = staff.value.employment_type || 'employee'
    staff.value.badge_title_override = String(staff.value.badge_title_override ?? '')
    badgeTitleMode.value = staff.value.badge_title_override.trim() ? 'custom' : 'primary'

    if (!staff.value.contact) {
      staff.value.contact = {}
    }
    staff.value.contact.addr = staff.value.contact?.address?.[0]?.data ?? staff.value.contact.addr ?? null

    selectedSkills.value = Array.isArray(staff.value.skills)
      ? staff.value.skills.map(skill => ({ id: Number(skill.id), name: skill.name }))
      : []
    if (selectedSkills.value.length > 0) {
      const hasPrimary = selectedSkills.value.some(
        skill => Number(skill.id) === Number(staff.value.primary_skill),
      )
      staff.value.primary_skill = hasPrimary
        ? Number(staff.value.primary_skill)
        : Number(selectedSkills.value[0].id)
    } else {
      staff.value.primary_skill = null
    }

    breadcrumbStore.setValue(staff.value.contact.name ?? '')

    const payload = getSavePayload()
    lastSavedSignature.value = payload ? getSaveSignature(payload) : ''
    lastSavedBadgeSignature.value = payload ? getBadgeSignature(payload) : ''

    await Promise.all([getPhoto(), getSkills(), getBadge()])
    ensureTabDataLoaded()
  } catch (error) {
    showToast('Unable to load staff profile.', 'error')
  } finally {
    await nextTick()
    isReady.value = true
  }
}

const loadJobsWorked = async ({ force = false } = {}) => {
  if (!staff.value.id || jobsLoading.value || (jobsLoaded.value && !force)) {
    return
  }

  jobsLoading.value = true
  jobsError.value = ''
  try {
    const response = await apiClient.get(`/api/staff/${staff.value.id}/jobs`)
    jobsWorked.value = Array.isArray(response.data) ? response.data : []
    jobsLoaded.value = true
  } catch (error) {
    jobsError.value = 'Unable to load jobs worked.'
  } finally {
    jobsLoading.value = false
  }
}

const loadPayments = async ({ force = false } = {}) => {
  if (
    !canViewPayments.value ||
    !staff.value.id ||
    paymentsLoading.value ||
    (paymentsLoaded.value && !force)
  ) {
    return
  }

  paymentsLoading.value = true
  paymentsError.value = ''
  try {
    const response = await apiClient.get(`/api/staff/${staff.value.id}/payments`)
    payments.value = Array.isArray(response.data) ? response.data : []
    paymentsLoaded.value = true
  } catch (error) {
    paymentsError.value = 'Unable to load payments.'
  } finally {
    paymentsLoading.value = false
  }
}

const ensureTabDataLoaded = (tabId = activeTab.value) => {
  if (tabId === 'jobs') {
    loadJobsWorked()
  }
  if (tabId === 'payments') {
    loadPayments()
  }
}

watch(
  () => route.query.tab,
  tabValue => {
    const nextTab = getValidTab(tabValue)
    activeTab.value = nextTab
    ensureTabDataLoaded(nextTab)
  },
  { immediate: true },
)

watch(
  () => route.params.id,
  () => {
    isReady.value = false
    lastSavedSignature.value = ''
    lastSavedBadgeSignature.value = ''
    jobsWorked.value = []
    jobsLoaded.value = false
    jobsError.value = ''
    payments.value = []
    paymentsLoaded.value = false
    paymentsError.value = ''
    getStaff()
  },
)

watch(
  () => staff.value?.contact?.name,
  name => {
    breadcrumbStore.setValue(name ?? '')
  },
)

watch(
  [staff, selectedSkills, badgeTitleMode],
  () => {
    if (!isReady.value) {
      return
    }
    debouncedSave()
  },
  { deep: true },
)

watch(
  () => canViewPayments.value,
  allowed => {
    if (!allowed && activeTab.value === 'payments') {
      setActiveTab(DEFAULT_TAB)
    }
  },
)

onMounted(() => {
  getStaff()
})

const deleteStaff = () => {
  apiClient.delete(`/api/staff/${staff.value.id}`)
  router.push('/staff')
}

const isSkillSelected = skill => {
  return selectedSkillIdSet.value.has(Number(skill.id))
}

const isPrimarySkill = skillId => {
  return Number(staff.value.primary_skill) === Number(skillId)
}

const setPrimarySkill = skillId => {
  if (!selectedSkillIdSet.value.has(Number(skillId))) {
    return
  }
  staff.value.primary_skill = Number(skillId)
}

const toggleSkillSelection = skill => {
  const skillId = Number(skill.id)
  const index = selectedSkills.value.findIndex(s => Number(s.id) === skillId)
  if (index > -1) {
    const removed = selectedSkills.value[index]
    selectedSkills.value.splice(index, 1) // Remove if it exists
    if (isPrimarySkill(removed.id)) {
      staff.value.primary_skill = selectedSkills.value[0]?.id ?? null
    }
  } else {
    selectedSkills.value.push({ id: skillId, name: skill.name }) // Add if it doesn't
    if (!staff.value.primary_skill) {
      staff.value.primary_skill = skillId
    }
  }
}

onBeforeUnmount(() => {
  if (toastTimeout) {
    clearTimeout(toastTimeout)
  }
  disableTimer()
})
</script>
<template>
  <div v-if="staff.id" class="staff-profile-view min-w-0 space-y-6">
    <form-box :header="staff.contact?.name + ' - #' + staff.id">
      <template v-slot:nav>
        <DeleteButton @delete="deleteStaff" />
      </template>
    </form-box>

    <div class="box sticky top-4 z-20 backdrop-blur bg-white/90 dark:bg-slate-900/90">
      <div class="flex flex-wrap gap-2 rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="min-w-0 flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition sm:flex-none sm:px-4"
            :class="activeTab === tab.id
              ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
              : 'text-slate-600 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white'"
            @click.prevent="setActiveTab(tab.id)"
          >
            <span>{{ tab.label }}</span>
            <span
              v-if="tab.id === 'jobs' && jobsLoaded"
              class="text-xs rounded-full px-2 py-0.5 bg-white/15 dark:bg-slate-700"
            >
              {{ jobsWorked.length }}
            </span>
            <span
              v-if="tab.id === 'payments' && paymentsLoaded"
              class="text-xs rounded-full px-2 py-0.5 bg-white/15 dark:bg-slate-700"
            >
              {{ payments.length }}
            </span>
          </button>
      </div>
    </div>

    <div v-show="activeTab === 'overview'" class="space-y-6 min-w-0">
      <div class="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-3">
        <form-box header="Profile Photo" class="min-w-0 xl:col-span-1">
          <div class="flex flex-col items-center gap-4">
            <div class="flex aspect-square w-full max-w-[14rem] items-center justify-center overflow-hidden rounded-xl border bg-gray-300">
              <img :src="photoUrl" v-if="photoUrl && photoUrl.length > 50" class="w-full h-full object-cover" />
              <div class="animate-pulse" v-if="photoUploading">
                <div class="h-4 bg-gray-300 rounded w-full h-full"></div>
              </div>
            </div>
            <div class="text-center">
              <input type="file" ref="fileInput" class="hidden" @change="onFileChange" accept="image/png, image/jpeg" />
              <EditButton @edit="$refs.fileInput.click()" title="Photo" />
              <div class="text-xs text-center">Accepts jpg/png</div>
            </div>
          </div>
        </form-box>

        <div class="flex min-w-0 flex-col gap-6 xl:col-span-2">
          <form-box header="Status">
            <form class="grid min-w-0 grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              <div>
                <label for="joined">Joined Date</label>
                <DateField :data="staff.joined_at" :editable="editable" @update="val => (staff.joined_at = val)" />
              </div>

              <div>
                <label for="induction">Induction Date</label>
                <DateField :data="staff.induction_at ?? null" :editable="editable"
                  @update="val => (staff.induction_at = val)" />
              </div>

              <div>
                <label for="active">Active</label>
                <input type="checkbox" v-model="staff.active" v-if="editable" />
                <span v-else>{{ staff.active ? 'Yes' : 'No' }}</span>
              </div>
            </form>
          </form-box>

          <form-box header="Onboarding">
            <form class="grid min-w-0 grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              <div class="staff-upload-field">
                <label for="contract">Contract</label>
                <input
                  type="file"
                  id="contract"
                  class="staff-file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="event => uploadOnboardingFile(event, 'contract', 'contract')"
                >
                <span v-if="staff.contract" class="staff-upload-meta">
                  <a :href="`${apiUrl}/files/${staff.contract.id}`" target="_blank">
                    Uploaded {{ dayjs(staff.contract.created_at).format('DD/MM/YYYY') }}
                  </a>
                </span>
              </div>
              <div class="staff-upload-field">
                <label for="workingTimeDirective">Working Time Directive</label>
                <input
                  type="file"
                  id="workingTimeDirective"
                  class="staff-file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="event => uploadOnboardingFile(event, 'workingTimeDirectiveOptOut', 'working_time_directive_opt_out')"
                >
                <span v-if="staff.working_time_directive_opt_out" class="staff-upload-meta">
                  <a :href="`${apiUrl}/files/${staff.working_time_directive_opt_out.id}`" target="_blank">
                    Uploaded {{ dayjs(staff.working_time_directive_opt_out.created_at).format('DD/MM/YYYY') }}
                  </a>
                </span>
              </div>
              <div class="staff-upload-field">
                <label for="pensionEnrollment">Pension Enrollment</label>
                <input
                  type="file"
                  id="pensionEnrollment"
                  class="staff-file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="event => uploadOnboardingFile(event, 'pensionAutoEnrollmentOptOut', 'pension_auto_enrollment_opt_out')"
                >
                <span v-if="staff.pension_auto_enrollment_opt_out" class="staff-upload-meta">
                  <a :href="`${apiUrl}/files/${staff.pension_auto_enrollment_opt_out.id}`" target="_blank">
                    Uploaded {{ dayjs(staff.pension_auto_enrollment_opt_out.created_at).format('DD/MM/YYYY') }}
                  </a>
                </span>
              </div>
              <div class="staff-upload-field">
                <label for="hmrcStarter">HMRC Starter</label>
                <input
                  type="file"
                  id="hmrcStarter"
                  class="staff-file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="event => uploadOnboardingFile(event, 'hmrcNewStarterP45', 'hmrc_new_starter_p45')"
                >
                <span v-if="staff.hmrc_new_starter_p45" class="staff-upload-meta">
                  <a :href="`${apiUrl}/files/${staff.hmrc_new_starter_p45.id}`" target="_blank">
                    Uploaded {{ dayjs(staff.hmrc_new_starter_p45.created_at).format('DD/MM/YYYY') }}
                  </a>
                </span>
              </div>
              <div class="staff-upload-field">
                <label for="reference1">Reference #1</label>
                <input
                  type="file"
                  id="reference1"
                  class="staff-file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="event => uploadOnboardingFile(event, 'reference1', 'reference1')"
                >
                <span v-if="staff.reference1" class="staff-upload-meta">
                  <a :href="`${apiUrl}/files/${staff.reference1.id}`" target="_blank">
                    Uploaded {{ dayjs(staff.reference1.created_at).format('DD/MM/YYYY') }}
                  </a>
                </span>
              </div>
              <div class="staff-upload-field">
                <label for="reference2">Reference #2</label>
                <input
                  type="file"
                  id="reference2"
                  class="staff-file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="event => uploadOnboardingFile(event, 'reference2', 'reference2')"
                >
                <span v-if="staff.reference2" class="staff-upload-meta">
                  <a :href="`${apiUrl}/files/${staff.reference2.id}`" target="_blank">
                    Uploaded {{ dayjs(staff.reference2.created_at).format('DD/MM/YYYY') }}<br>
                  </a>
                </span>
              </div>
            </form>
          </form-box>
        </div>
      </div>

      <form-box header="Notes">
        <textarea v-model="staff.notes" class="w-full min-h-24" v-if="editable" />
        <span v-else>{{ staff.notes ?? 'No notes available' }}</span>
      </form-box>
    </div>

    <div v-show="activeTab === 'employment'" class="space-y-6">
      <form-box header="Employment Type">
        <div class="max-w-3xl">
          <p class="text-sm text-slate-600 dark:text-slate-300">
            Employees are expected to submit timesheets for assigned jobs. Contractors invoice outside payroll and are excluded from missing-timesheet warnings.
          </p>
          <div class="mt-4 inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-900">
            <button
              v-for="option in [{ value: 'employee', label: 'Employee' }, { value: 'contractor', label: 'Contractor' }]"
              :key="option.value"
              type="button"
              class="rounded-lg px-4 py-2 text-sm font-semibold transition"
              :class="staff.employment_type === option.value
                ? 'bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'"
              :disabled="!editable"
              @click.prevent="staff.employment_type = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </form-box>

      <div class="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
        <form-box header="Personal Information">
          <form class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-full">
            <div>
              <label for="name">Name</label>
              <TextField :data="staff.contact.name" :editable="editable" @update="val => (staff.contact.name = val)" />
            </div>

            <div>
              <label for="name">Nickname</label>
              <TextField :data="staff.contact.nickname" :editable="editable"
                @update="val => (staff.contact.nickname = val)" />
            </div>

            <div>
              <label for="email">Email Address</label>
              <TextField :data="staff.contact.email ?? null" :editable="editable"
                @update="val => (staff.contact.email = val)" />
            </div>

            <div>
              <label for="phone">Phone Number</label>
              <TextField :data="staff.contact.phone ?? null" :editable="editable"
                @update="val => (staff.contact.phone = val)" />
            </div>
            <div class="">
              <label for="sex">Sex</label>
              <select id="sex" v-model="staff.contact.sex"
                class="shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
                v-if="editable">
                <option value="0">Male</option>
                <option value="1">Female</option>
              </select>
              <template v-else>
                <span v-if="staff.contact.sex == 0">Male</span>
                <span v-else>Female</span>
              </template>
            </div>
            <div>
              <label title="Date of Birth">DOB</label>
              <DateField :data="staff.contact.dob ?? null" :editable="editable"
                @update="val => (staff.contact.dob = val)" />
            </div>
            <div>
              <label>National Insurance</label>
              <TextField :data="staff.national_insurance ?? null" :editable="editable"
                @update="val => (staff.national_insurance = val)" />
            </div>
            <div>
              <label>Tax Code</label>
              <TextField :data="staff.tax_code ?? null" :editable="editable"
                @update="val => (staff.tax_code = val)" />
              <p v-if="staff.tax_code" class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {{ staff.tax_code_source_payslip_id ? 'Extracted from the latest matched payslip' : 'Set manually' }}
                <span v-if="staff.tax_code_updated_at"> · {{ dayjs(staff.tax_code_updated_at).format('DD MMM YYYY') }}</span>
              </p>
            </div>
            <div>
              <label>NI Category</label>
              <TextField :data="staff.national_insurance_category ?? null" :editable="editable"
                @update="val => (staff.national_insurance_category = val)" />
              <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Usually A; this is also extracted from matched payslips.
              </p>
            </div>
            <div>
              <label>Registration Number</label>
              <TextField :data="staff.registration_number ?? null" :editable="editable"
                @update="val => (staff.registration_number = val)" />
            </div>
            <div>
              <label for="contact_address">Address</label>
              <textarea class="w-full" id="contact_address" v-model="staff.contact.addr"
                :disabled="!editable"></textarea>
            </div>
          </form>
        </form-box>

        <form-box header="Safety Checks">
          <form class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl">
            <div>
              <label for="dbsNumber">DBS Number</label>
              <TextField :data="staff.dbsNumber ?? null" :editable="editable" @update="val => (staff.dbsNumber = val)" />
            </div>

            <div class="">
              <label for="dbsDate">DBS Date</label>
              <DateField :data="staff.dbsDate ?? null" :editable="editable" @update="val => (staff.dbsDate = val)" />
            </div>
            <div class="">
              <label for="dbsUpdateService">Update Service</label>
              <select id="dbsUpdateService" v-model="staff.dbsUpdateService"
                class="shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
                v-if="editable">
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
              <template v-else>
                <span v-if="staff.dbsUpdateService == 0">No</span>
                <span v-else>Yes</span>
              </template>
            </div>

            <div class="">
              <label for="drivingLicenseCheck">Driving License Check</label>
              <DateField :data="staff.drivingLicenseCheck ?? null" :editable="editable"
                @update="val => (staff.drivingLicenseCheck = val)" />
            </div>
            <div class="">
              <label for="drivingAssessment">Driving Assessment</label>
              <DateField :data="staff.drivingAssessment ?? null" :editable="editable"
                @update="val => (staff.drivingAssessment = val)" />
            </div>
          </form>
        </form-box>
      </div>

      <div class="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
        <form-box header="Uniform">
          <form class="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl">
            <div>
              <label>Shirt</label>
              <select v-model="staff.uniform_shirt" v-if="editable">
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
                <option value="XXXL">XXXL</option>
              </select>
              <span v-else>{{ staff.uniform_shirt }}</span>
            </div>
            <div>
              <label>Jacket</label>
              <select v-model="staff.uniform_jacket" v-if="editable">
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
                <option value="XXXL">XXXL</option>
              </select>
              <span v-else>{{ staff.uniform_jacket }}</span>
            </div>
            <div>
              <label>Trousers</label>
              <select v-model="staff.uniform_trousers" v-if="editable">
                <option value="26 inch">26 inch</option>
                <option value="28 inch">28 inch</option>
                <option value="30 inch">30 inch</option>
                <option value="31 inch">31 inch</option>
                <option value="32 inch">32 inch</option>
                <option value="33 inch">33 inch</option>
                <option value="34 inch">34 inch</option>
                <option value="36 inch">36 inch</option>
                <option value="38 inch">38 inch</option>
                <option value="40 inch">40 inch</option>
                <option value="42 inch">42 inch</option>
                <option value="44 inch">44 inch</option>
                <option value="46 inch">46 inch</option>
                <option value="48 inch">48 inch</option>
                <option value="Womens 8">Womens 8</option>
                <option value="Womens 10">Womens 10</option>
                <option value="Womens 12">Womens 12</option>
                <option value="Womens 14">Womens 14</option>
                <option value="Womens 16">Womens 16</option>
                <option value="Womens 18">Womens 18</option>
              </select>
              <span v-else>{{ staff.uniform_trousers }}</span>
            </div>
          </form>
        </form-box>

        <form-box header="Emergency Contacts">
          <SearchContact @selected="addEmergencyContact" />
          <div class="overflow-x-auto">
            <TableList :modelValue="staff.contact.emergency_contacts" :cols="[
              { name: 'Name', row: 'name', editable: true },
              { name: 'Email', row: 'email', editable: true },
              { name: 'Phone', row: 'phone', editable: true },
            ]" :deletable="true" :box="false" @delete="deleteEmergencyContact" />
          </div>
        </form-box>
      </div>
    </div>

    <div v-show="activeTab === 'compliance'" class="space-y-6">
      <div class="space-y-6">
        <form-box header="Skills">
          <div class="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)]">
            <div class="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 dark:border-slate-700 dark:bg-slate-900/30">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div class="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                    Selected Skills
                  </div>
                  <div class="mt-2 text-3xl font-semibold text-slate-900 dark:text-slate-100">
                    {{ selectedSkills.length }}
                  </div>
                </div>
                <div class="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  Primary:
                  {{ selectedSkills.find(skill => isPrimarySkill(skill.id))?.name ?? "Not set" }}
                </div>
              </div>

              <div class="mt-5">
                <div class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Active set
                </div>
                <div v-if="selectedSkills.length" class="mt-3 flex min-h-[8rem] flex-wrap content-start gap-2">
                  <div
                    v-for="skill in selectedSkills"
                    :key="'selected-' + skill.id"
                    class="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 dark:border-slate-600 dark:bg-slate-800"
                  >
                    <span class="text-sm">{{ skill.name }}</span>
                    <button
                      type="button"
                      class="text-xs px-2 py-0.5 rounded-full border"
                      :class="isPrimarySkill(skill.id)
                        ? 'bg-sky-100 border-sky-300 text-sky-700 dark:bg-sky-900/50 dark:border-sky-700 dark:text-sky-200'
                        : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300'"
                      :disabled="!editable"
                      @click.prevent="setPrimarySkill(skill.id)"
                    >
                      {{ isPrimarySkill(skill.id) ? 'Primary' : 'Set primary' }}
                    </button>
                  </div>
                </div>
                <div v-else class="mt-3 rounded-xl border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  No skills selected yet.
                </div>
                <div class="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Primary skill shows on the ID badge and is used across staffing views.
                </div>
              </div>
            </div>

            <div class="min-w-0 rounded-2xl border border-slate-200 bg-white/70 p-5 dark:border-slate-700 dark:bg-slate-800/40">
              <div class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div class="min-w-0 flex-1">
                  <label for="skill-search">Search Skills</label>
                  <input
                    id="skill-search"
                    v-model="skillSearch"
                    type="text"
                    placeholder="Type to filter skills..."
                    class="mt-1 w-full"
                  />
                </div>
                <div class="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                  {{ filteredSkills.length }} available
                </div>
              </div>

              <div class="mt-4 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
                <div class="max-h-[28rem] overflow-y-auto">
                  <div
                    v-for="skill in filteredSkills"
                    :key="skill.id"
                    class="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 transition-colors hover:bg-gray-100 dark:border-slate-800 dark:hover:bg-gray-900"
                    :class="isSkillSelected(skill) ? 'bg-slate-50 dark:bg-slate-800/60' : ''"
                  >
                    <label :for="'checkbox-' + skill.id" class="flex min-w-0 flex-1 items-center gap-3">
                      <input
                        :id="'checkbox-' + skill.id"
                        type="checkbox"
                        class="form-checkbox"
                        :disabled="!editable"
                        :checked="isSkillSelected(skill)"
                        @change="toggleSkillSelection(skill)"
                      />
                      <span class="truncate font-medium">{{ skill.name }}</span>
                    </label>
                    <button
                      v-if="isSkillSelected(skill)"
                      type="button"
                      class="text-xs px-2 py-1 rounded border"
                      :class="isPrimarySkill(skill.id)
                        ? 'bg-sky-100 border-sky-300 text-sky-700 dark:bg-sky-900/50 dark:border-sky-700 dark:text-sky-200'
                        : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300'"
                      :disabled="!editable"
                      @click.prevent="setPrimarySkill(skill.id)"
                    >
                      {{ isPrimarySkill(skill.id) ? 'Primary' : 'Set primary' }}
                    </button>
                  </div>
                  <div v-if="filteredSkills.length === 0" class="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
                    No skills match your search.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form-box>

        <form-box header="Training">
          <TrainingCourses :staff="staff" />
        </form-box>
      </div>

      <form-box header="ID Badge">
        <div class="flex flex-col gap-8 border-0 mx-auto md:flex-row md:items-center">
          <img :src="badge.image" class="w-full max-h-64 object-contain" v-if="badge.image" />
          <div
            class="h-64 w-full p-8 bg-gray-300 flex ring-2 ring-gray-300 dark:ring-gray-500 rounded text-center justify-center items-center"
            v-else>
            Badge does not exist and must be generated
          </div>
          <div class="w-full">
            <div class="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white/70 dark:bg-slate-800/60 mb-4">
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Badge Title
              </div>
              <div class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label
                  class="flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer"
                  :class="badgeTitleMode === 'primary'
                    ? 'border-sky-300 bg-sky-50 dark:border-sky-700 dark:bg-sky-900/30'
                    : 'border-slate-200 dark:border-slate-700'"
                >
                  <input
                    v-model="badgeTitleMode"
                    type="radio"
                    value="primary"
                  />
                  <span class="text-sm">Use primary skill</span>
                </label>

                <label
                  class="flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer"
                  :class="badgeTitleMode === 'custom'
                    ? 'border-sky-300 bg-sky-50 dark:border-sky-700 dark:bg-sky-900/30'
                    : 'border-slate-200 dark:border-slate-700'"
                >
                  <input
                    v-model="badgeTitleMode"
                    type="radio"
                    value="custom"
                  />
                  <span class="text-sm">Use custom text</span>
                </label>
              </div>

              <div v-if="badgeTitleMode === 'custom'" class="mt-3">
                <label for="badge_title_override">Custom Badge Title</label>
                <input
                  id="badge_title_override"
                  v-model="staff.badge_title_override"
                  type="text"
                  maxlength="40"
                  placeholder="Example: Team Lead"
                  class="w-full mt-1"
                />
                <div class="text-xs text-slate-500 mt-1">
                  {{ badgeTitleLength }}/40 characters
                </div>
              </div>
            </div>

            <div v-if="badge && badge.meta">
              <label>Created</label>
              <span>{{ new Date(badge.meta.created_at).toLocaleString() }}</span>
              <label class="mt-4">Expiry</label>
              <span>{{ new Date(badge.meta.expiration).toLocaleString() }}</span>
            </div>
            <br />
            <br />
            <div class="flex flex-col gap-3 sm:flex-row">
              <button class="btn" @click.prevent="generateIdBadge">
                Generate
              </button>
              <DownloadButton :url="'/api/staff/' + staff.id + '/badge?download=1'"
                :filename="staff.contact.name + ' ID badge.png'" />
            </div>
            <br />
            <div class="py-4 text-xs">
              Generating a new ID Badge will void any exising ID badge. A voided
              ID badge will shown "Status: Void" when the QR Code is scanned.
            </div>
          </div>
        </div>
      </form-box>
    </div>

    <div v-show="activeTab === 'jobs'" class="space-y-6">
      <form-box header="Jobs Worked">
        <template v-slot:nav>
          <button class="btn" @click.prevent="refreshActiveTabData">
            Refresh
          </button>
        </template>

        <div v-if="jobsLoading" class="text-sm text-slate-500 py-6">
          Loading jobs worked...
        </div>
        <div v-else-if="jobsError" class="text-sm text-rose-600 py-6">
          {{ jobsError }}
        </div>
        <div v-else-if="jobsWorked.length === 0" class="text-sm text-slate-500 py-6">
          No jobs found for this staff member.
        </div>
        <template v-else>
          <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
            <div class="xl:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white/70 dark:bg-slate-800/60">
              <div class="text-sm font-semibold mb-3">
                Jobs Per Month (Last 12 Months)
              </div>
              <JobsTrendChart
                :labels="jobsTrendData.labels"
                :totals="jobsTrendData.totals"
                :personnel="jobsTrendData.personnel"
                :drivers="jobsTrendData.drivers"
              />
            </div>

            <div class="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white/70 dark:bg-slate-800/60">
              <div class="text-sm font-semibold mb-3">
                Role Mix
              </div>
              <JobsRoleMixChart
                :labels="jobsRoleMixData.labels"
                :values="jobsRoleMixData.values"
              />
            </div>
          </div>

          <div class="space-y-3 lg:hidden">
            <div
              v-for="job in jobsWorked"
              :key="job.id"
              class="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white/70 dark:bg-slate-800/60 hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="flex items-center gap-2 flex-wrap">
                  <ReferenceNumber
                    v-if="job.reference?.id && job.reference?.txt"
                    :reference="job.reference"
                  />
                  <div class="font-semibold">
                    {{ job.reference?.name ?? 'Reference' }}
                  </div>
                </div>
                <span class="label-generic">{{ job.role ?? 'Staff' }}</span>
              </div>
              <div class="text-sm mt-2">
                {{ job.date ? dayjs(job.date).format('DD MMM YYYY') : 'Date unavailable' }}
              </div>
              <div class="text-xs text-slate-500 mt-1">
                {{ job.source === 'driver' ? 'Driver allocation' : 'Personnel assignment' }}
              </div>
              <div
                v-if="hasPayrollClaims(job)"
                class="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100"
              >
                <div class="font-semibold">Payroll: {{ payrollClaimHours(job) }}h claimed</div>
                <div class="mt-1">{{ payrollClaimSummary(job) }}</div>
              </div>
            </div>
          </div>

          <div class="hidden lg:block overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-700">
                  <th class="p-3">Date</th>
                  <th class="p-3">Reference</th>
                  <th class="p-3">Role</th>
                  <th class="p-3">Source</th>
                  <th v-if="canViewPayments" class="p-3">Payroll Claims</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="job in jobsWorked"
                  :key="job.id"
                  class="border-b border-slate-100 dark:border-slate-800 hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
                >
                  <td class="p-3">
                    {{ job.date ? dayjs(job.date).format('DD MMM YYYY') : 'Date unavailable' }}
                  </td>
                  <td class="p-3">
                    <div class="flex items-center gap-2 flex-wrap">
                      <ReferenceNumber
                        v-if="job.reference?.id && job.reference?.txt"
                        :reference="job.reference"
                      />
                      <div class="font-semibold">{{ job.reference?.name ?? 'Reference' }}</div>
                    </div>
                  </td>
                  <td class="p-3">{{ job.role ?? 'Staff' }}</td>
                  <td class="p-3 capitalize">{{ job.source ?? 'personnel' }}</td>
                  <td v-if="canViewPayments" class="p-3">
                    <div v-if="hasPayrollClaims(job)" class="space-y-1">
                      <div class="font-semibold text-emerald-700 dark:text-emerald-300">
                        {{ payrollClaimHours(job) }}h claimed
                      </div>
                      <div class="text-xs text-slate-500 dark:text-slate-400">
                        {{ payrollClaimSummary(job) }}
                      </div>
                    </div>
                    <span v-else class="text-xs text-slate-400">No claim</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </form-box>
    </div>

    <div v-show="activeTab === 'payments' && canViewPayments" class="space-y-6">
      <form-box header="Payments">
        <template v-slot:nav>
          <button class="btn" @click.prevent="refreshActiveTabData">
            Refresh
          </button>
        </template>

        <div v-if="paymentsLoading" class="text-sm text-slate-500 py-6">
          Loading payments...
        </div>
        <div v-else-if="paymentsError" class="text-sm text-rose-600 py-6">
          {{ paymentsError }}
        </div>
        <div v-else-if="payments.length === 0" class="text-sm text-slate-500 py-6">
          No payments recorded for this staff member.
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-700">
                <th class="p-3">Period</th>
                <th class="p-3 text-right">Gross</th>
                <th class="p-3 text-right">Net</th>
                <th class="p-3">Status</th>
                <th class="p-3">Paid At</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="payment in payments"
                :key="payment.id"
                class="border-b border-slate-100 dark:border-slate-800"
              >
                <td class="p-3">
                  {{ payment.period_label ?? 'Period unavailable' }}
                </td>
                <td class="p-3 text-right">{{ payment.gross_amount ?? '-' }}</td>
                <td class="p-3 text-right">{{ payment.net_amount ?? '-' }}</td>
                <td class="p-3">{{ payment.status ?? '-' }}</td>
                <td class="p-3">
                  {{ payment.paid_at ? dayjs(payment.paid_at).format('DD MMM YYYY') : '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </form-box>
    </div>
  </div>

  <div
    v-if="toast.visible"
    class="fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-xl text-sm font-medium flex items-center gap-2 z-50"
    :class="toast.tone === 'error' ? 'bg-rose-600 text-white' : 'bg-emerald-600 text-white'"
  >
    <svg
      v-if="toast.tone !== 'error'"
      class="h-4 w-4 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
    </svg>
    <svg
      v-else
      class="h-4 w-4 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <span>{{ toast.message }}</span>
  </div>
</template>

<style scoped>
.staff-profile-view {
  overflow-x: clip;
}

.staff-file-input {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  font-size: 0.875rem;
}

.staff-upload-field {
  min-width: 0;
}

.staff-upload-meta {
  display: block;
  margin-top: 0.5rem;
  overflow-wrap: anywhere;
  font-size: 0.875rem;
}

@media (max-width: 640px) {
  .staff-profile-view :deep(.box) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
